'use client'

import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging'
import { app } from './firebase-client'
import { apiFetch } from './api'

const VAPID_KEY = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY || ''

// Track the active foreground-message listener so repeated registerForPush()
// calls don't stack handlers (which would fire duplicate notifications).
let unsubscribeOnMessage: (() => void) | null = null

function buildSwUrl(): string {
  const params = new URLSearchParams({
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY || '',
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID || '',
  })
  return `/firebase-messaging-sw.js?${params.toString()}`
}

export async function isPushSupported(): Promise<boolean> {
  if (typeof window === 'undefined') return false
  if (!('serviceWorker' in navigator)) return false
  if (!('Notification' in window)) return false
  try {
    return await isSupported()
  } catch {
    return false
  }
}

export async function registerForPush(): Promise<string | null> {
  if (!(await isPushSupported())) return null
  if (!VAPID_KEY) {
    console.warn('NEXT_PUBLIC_FIREBASE_VAPID_KEY missing')
    return null
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return null

  const reg = await navigator.serviceWorker.register(buildSwUrl(), { scope: '/' })
  await navigator.serviceWorker.ready

  const messaging = getMessaging(app)
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg })
  if (!token) return null

  // Save to user doc
  try {
    await apiFetch('/api/fcm/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
  } catch (e) {
    console.error('Failed to register token', e)
  }

  // Foreground messages → in-app toast. Tear down any previous listener first so
  // calling registerForPush() more than once doesn't stack duplicate handlers.
  if (unsubscribeOnMessage) {
    unsubscribeOnMessage()
    unsubscribeOnMessage = null
  }
  unsubscribeOnMessage = onMessage(messaging, (payload) => {
    const title = payload.notification?.title || payload.data?.title || 'Sasha Infinity'
    const body = payload.notification?.body || payload.data?.body || ''
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/icons/icon-192.png' })
    }
  })

  return token
}

export async function unregisterPush(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker.getRegistration('/')
    if (!reg) return
    const messaging = getMessaging(app)
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg }).catch(() => null)
    if (token) {
      await apiFetch('/api/fcm/unregister', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
    }
  } catch (e) { console.error(e) }
}
